import React from 'react';
import PropTypes from 'prop-types';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  Modal,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useScrollTrigger,
  Slide,
  CssBaseline,
  Container
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Home as HomeIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Calculate as CalculateIcon, // Added for Calculators
  Newspaper as NewspaperIcon, // Added for News
} from '@mui/icons-material';
import { createTheme } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import SignupForm from '../SignupForm/SignupForm' // Assuming SignupForm is in the same directory
import LoginForm from '../LoginForm/LoginForm'
import { useAuth } from '../../services/authContext';

// --- THEME DEFINITION ---
export const getAppTheme = (mode = 'light') => {
  return createTheme({
    palette: {
      mode,
      primary: { main: '#5c97c3' },
      secondary: { main: '#3f51b5' },
      background: {
        default: mode === 'dark' ? '#121212' : '#f8f9fa',
        paper: mode === 'dark' ? '#1e1e1e' : '#ffffff',
      },
      text: {
        primary: mode === 'dark' ? '#e0e0e0' : '#212529',
        secondary: mode === 'dark' ? '#a0a0a0' : '#6c757d',
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h6: { fontWeight: 600 },
    },
  });
};

// --- AUTH MODAL COMPONENT ---
const AuthModal = ({ open, onClose, theme }) => {
  const [isLoginView, setIsLoginView] = React.useState(true);
  const toggleView = () => setIsLoginView(!isLoginView);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        maxWidth: 400,
        bgcolor: 'background.paper',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '16px',
        boxShadow: 24,
        py: 3,
      }}>
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8 }}>
          <CloseIcon />
        </IconButton>
        <Typography variant="h5" component="h2" textAlign="center" gutterBottom>
          {isLoginView ? 'Sign In' : 'Create Account'}
        </Typography>
        {isLoginView ? <LoginForm /> : <SignupForm />}
        <Typography variant="body2" textAlign="center">
          {isLoginView ? "Don't have an account?" : "Already have an account?"}
          <Button onClick={toggleView} sx={{ textTransform: 'none', ml: 0.5 }}>
            {isLoginView ? 'Sign Up' : 'Sign In'}
          </Button>
        </Typography>
      </Box>
    </Modal>
  );
};

// --- HIDE ON SCROLL COMPONENT ---
function HideOnScroll(props) {
  const { children, window } = props;
  const trigger = useScrollTrigger({ target: window ? window() : undefined });
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

HideOnScroll.propTypes = {
  children: PropTypes.element.isRequired,
  window: PropTypes.func,
};

// --- HEADER COMPONENT ---
const Header = (props) => {
  const { window } = props;
  const { loginStatus } = useAuth();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [openAuth, setOpenAuth] = React.useState(false);

  const location = useLocation();
  const currentPath = location.pathname;

  const theme = getAppTheme('light');

  // --- UPDATED NAVIGATION LINKS WITH NEW ORDER AND ICONS ---
  const navLinks = [
    { label: 'Home', path: '/', icon: <HomeIcon /> },
    { label: 'Calculators', path: '/calc', icon: <CalculateIcon /> },
    { label: 'News', path: '/news', icon: <NewspaperIcon /> },
  ];

  const handleDrawerToggle = () => setMobileOpen((prevState) => !prevState);
  const handleAuthOpen = () => setOpenAuth(true);
  const handleAuthClose = () => setOpenAuth(false);

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography variant='h6' sx={{ my: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2646ba' }}>
        {/* <Link to="/"><img src='/mle.jpg' alt='Logo' style={{ width: '35px', height: '35px', marginRight: '8px' }} /></Link> */}
        Make Loan Eazy.
      </Typography>
      <Divider />
      <List>
        {navLinks.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              sx={{
                textAlign: 'center',
                // Make room for the styled button in the drawer
                ...(item.label === 'News' && { p: 1 })
              }}
            >
              {item.label === 'News' ? (
                <Button
                  variant="contained"
                  startIcon={item.icon}
                  fullWidth
                  sx={{
                    color: 'white',
                    background: 'linear-gradient(45deg, #ef5350 30%, #ff7043 90%)',
                    boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
                  }}
                >
                  {item.label}
                </Button>
              ) : (
                <ListItemText primary={item.label} />
              )}
            </ListItemButton>
          </ListItem>
        ))}
        <Divider sx={{ my: 1 }} />
        {loginStatus.token ? (
          <>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/settings" sx={{ justifyContent: 'center' }}>
                <Button variant="outlined" fullWidth>Settings</Button>
              </ListItemButton>
            </ListItem>
            {loginStatus.user.staff_type === 'admin' && (
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/admin" sx={{ justifyContent: 'center' }}>
                  <Button variant="contained" fullWidth>Admin</Button>
                </ListItemButton>
              </ListItem>
            )}
          </>
        ) : (
          <ListItem disablePadding>
            <ListItemButton sx={{ justifyContent: 'center' }} onClick={handleAuthOpen}>
              <Button variant="contained" fullWidth>Sign In</Button>
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <React.Fragment>
      <CssBaseline />
      <HideOnScroll {...props}>
        <AppBar
          component='nav'
          sx={{
            background: theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.7)' : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            boxShadow: 'none',
            borderBottom: `1px solid ${theme.palette.divider}`,
            minHeight: "56.8px !important",
          }}
        >
          <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ minHeight: "56.8px !important" }}>
              <Box component={Link} to="/" sx={{ flexGrow: { xs: 1, md: 0 }, display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'text.primary' }}>
                {/* <img src='/mle.jpg' alt='Logo' style={{ width: '40px', height: '40px' }} /> */}
                <Typography variant="h6" sx={{ fontWeight: 'bold', ml: 1.5, color: '#2646ba' }}>
                  Make Loan Eazy
                </Typography>
              </Box>

              <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'center' }}>
                {navLinks.map(link => (
                  <Button
                    key={link.label}
                    component={Link}
                    to={link.path}
                    variant={link.label === 'News' ? 'contained' : 'text'}
                    startIcon={link.icon}
                    sx={{
                      mx: 1.5,
                      fontWeight: currentPath === link.path ? 'bold' : 'normal',
                      position: 'relative',
                      // --- STYLES FOR ATTRACTIVE NEWS BUTTON ---
                      ...(link.label === 'News' ? {
                        color: 'white',
                        background: 'linear-gradient(45deg, #ef5350 30%, #ff7043 90%)',
                        boxShadow: '0 4px 14px 0 rgba(239, 83, 80, 0.39)',
                        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: '0 6px 20px 0 rgba(239, 83, 80, 0.45)',
                        }
                      } : {
                        color: 'text.primary',
                      }),
                      // Underline for other buttons
                      '&::after': {
                        display: link.label === 'News' ? 'none' : 'block',
                        content: '""',
                        position: 'absolute',
                        width: '100%',
                        transform: currentPath === link.path ? 'scaleX(1)' : 'scaleX(0)',
                        height: '2px',
                        bottom: 0,
                        left: 0,
                        backgroundColor: 'primary.main',
                        transformOrigin: 'bottom center',
                        transition: 'transform 0.25s ease-out',
                      }
                    }}
                  >
                    {link.label}
                  </Button>
                ))}
              </Box>

              {/* Desktop Auth/Admin Buttons */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                <Button component={Link} to="/lamf" variant="outlined" color="primary" sx={{ mr: 1.5 }}>Loan Against Mutual Fund</Button>
                {loginStatus.token ? (
                  <>
                    {loginStatus.user.staff_type === 'admin' && (
                      <Button component={Link} to="/admin" variant="contained" color="secondary" sx={{ mr: 1.5 }}>Admin</Button>
                    )}
                    <IconButton component={Link} to="/settings" color="primary"><SettingsIcon /></IconButton>
                    {loginStatus.user.staff_type === 'user' && (
                      <IconButton component={Link} to="/wallet" color='primary'><AccountBalanceWalletIcon /></IconButton>
                    )}
                  </>
                ) : (
                  <>
                    <Button variant="contained" onClick={handleAuthOpen}>Sign In</Button>
                  </>
                )}
              </Box>

              {/* Mobile Buttons */}


              <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>

                <IconButton sx={{
                  color: 'white',
                  background: 'linear-gradient(45deg, #ef5350 30%, #ff7043 90%)',
                  boxShadow: '0 4px 14px 0 rgba(239, 83, 80, 0.39)',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  borderRadius: "6px",
                  marginRight: "10px"
                }} color='primary' aria-label='open drawer' component={Link} to="/news" >
                  <NewspaperIcon />
                </IconButton>

                <Button component={Link} to="/lamf" variant="outlined" size="small" sx={{ mr: 1 }}>LAMF</Button>
                <IconButton color='primary' aria-label='open drawer' edge='end' onClick={handleDrawerToggle}>
                  <MenuIcon />
                </IconButton>

              </Box>

            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>

      <nav>
        <Drawer
          container={container}
          variant='temporary'
          open={mobileOpen}
          onClose={handleDrawerToggle}
          anchor="right"
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 } }}
        >
          {drawer}
        </Drawer>
      </nav>

      <AuthModal open={openAuth} onClose={handleAuthClose} theme={theme} />
    </React.Fragment>
  );
};

Header.propTypes = {
  window: PropTypes.func,
};

export default Header;
